import { useEffect, useState } from "react";
import { isAbortError } from "../../api/http/httpClient";
import { toUiError } from "../../errors/uiError";
import type { LoadState } from "./useLoad";

export const TYPING_PAUSE_MILLISECONDS = 400;

export type PausedLoadState<T> = Exclude<LoadState<T>, { readonly status: "loading" }>;

/**
 * Run `load` once typing pauses, for search-as-you-type. The returned stop cancels both the
 * pause and the request; a late answer is ignored even if a transport ignores the abort.
 */
export function loadAfterTypingPause<T>(load: (signal: AbortSignal) => Promise<T>, receive: (state: PausedLoadState<T>) => void): () => void {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => {
    load(controller.signal).then(
      (value) => { if (!controller.signal.aborted) receive({ status: "loaded", value }); },
      (caught: unknown) => {
        if (!controller.signal.aborted && !isAbortError(caught)) receive({ status: "failed", error: toUiError(caught) });
      },
    );
  }, TYPING_PAUSE_MILLISECONDS);
  return () => { globalThis.clearTimeout(timer); controller.abort(); };
}

/** The trimmed query once typing pauses, for a `useLoad` keyed on it; clearing takes effect at once. */
export function useQueryAfterTypingPause(query: string): string {
  const [paused, setPaused] = useState(query.trim());
  useEffect(() => {
    const next = query.trim();
    const timer = globalThis.setTimeout(() => { setPaused(next); }, next === "" ? 0 : TYPING_PAUSE_MILLISECONDS);
    return () => { globalThis.clearTimeout(timer); };
  }, [query]);
  return paused;
}
